//
import { StyleSheet, css } from '@coursera/aphrodite/no-important';

import classNames from 'classnames';
import transition from 'src/styles/transition';
import {
  coursera,
  strawberry,
  peach,
  pear,
  kiwi,
  sea,
  blueberry,
  grape,
  productCardSpecialization,
} from 'src/styles/gradients';

// Easier to write with ...css than always use className as it's not strictly className
function cssWithClass(...args) {
  /**
   * This function has been updated in https://phabricator.dkandu.me/D103061
   * It used to have a condition checking whether the first argument is a string, and
   * pass the rest to aphrodite. Aphrodite does not handle strings (class names)!
   */
  const styles = args.reduce((all, item) => (typeof item !== 'string' ? [...all, item] : all), []);
  const aphroditeClassName = css(...styles);
  const otherClassNames = args.reduce((all, item) => (typeof item === 'string' ? [...all, item] : all), []);
  return { className: classNames(aphroditeClassName, ...otherClassNames) || undefined };
}

export { StyleSheet };
export { cssWithClass as css };
export { cssWithClass };
export { transition };

const containerMaxWidth = {
  xs: 288,
  sm: 576,
  md: 792,
  lg: 1008, // 936, 960 doesn't fit for 12 points
  xl: 1152,
  xxl: 1440,
};

const color = {
  primary: '#2A73CC',
  darkPrimary: '#0156B8',
  lightPrimary: '#82A8E1',
  accent: '#3BAFDA',
  aliceBlue: '#F5F7FA',
  navyBlue: '#263674',
  skyBlue: '#1E9EF5',
  lightCyan: '#E7FDFF',
  deepAzure: '#083E7D',

  primaryText: '#1F1F1F',
  primaryTextThemeDark: '#FFFFFF',
  secondaryText: '#666666',
  secondaryTextThemeDark: '#F5F7FA',
  lightGrayText: '#969696',

  divider: '#E1E1E1',
  dividerLight: '#F0F0F0',
  dividerThemeDark: '#BDBDBD',
  border: '#BDBDBD',
  borderThemeDark: '#E1E1E1',
  borderFocused: '#2A73CC',

  disabled: '#BDBDBD',
  disabledThemeDark: '#646668',
  disabledText: '#FDFEFF',
  disabledTextThemeDark: '#E3E4E5',

  bgGray: '#F5F5F5',
  bgGrayThemeMid: '#888888',
  bgGrayThemeDark: '#757575',
  bgDarkGray: '#666666',
  bgPaper: '#F5F5F5',

  icon: '#363B42',
  iconThemeDark: '#EBECED',
  shadow: '#E6E9ED',

  lightGray: '#FDFEFF',
  gray: '#EBECED',
  darkGray: '#CCD1D9',
  white: '#FFF',
  black: '#1F1F1F',

  warning: '#F7BB56',
  danger: '#E85628', // TODO rename as 'alert'
  error: '#D30001',
  success: '#1F8354',
  info: '#20589D',

  bgWarning: '#FCEBCE',
  bgDanger: '#FBE5DF', // TODO rename as 'alert'
  bgError: '#FFE0E5',
  bgSuccess: '#DFF3EA',
  bgInfo: '#EAF4FE',

  admin: '#0D47A1',
  superUser: '#9C27B0',

  // b2c: '#37BC9B',
  // b2b: '#5D9CEC',
  // degree: '#3BAFDA',
};
export { color };

const spacing = {
  minWidth: 360, // Smallest screen we want to support
  xxs: '0.1875rem',
  xs: '0.375rem',
  sm: '0.75rem',
  md: '1.125rem',
  lg: '1.5rem',
  xl: '3rem',
  xxl: '4.5rem',
};
export { spacing };

const iconSize = {
  xxs: 14,
  xs: 18,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  xxl: 128,
};
export { iconSize };

const breakPoint = {
  xs: containerMaxWidth.xs + 32,
  sm: containerMaxWidth.sm + 32,
  md: containerMaxWidth.md + 32,
  lg: containerMaxWidth.lg + 32,
  xl: containerMaxWidth.xl + 32,
  xxl: containerMaxWidth.xxl + 32,
};

export { breakPoint };

export { containerMaxWidth };
const gradient = {
  primary: coursera,
  secondary: strawberry,
  coursera,
  strawberry,
  peach,
  pear,
  kiwi,
  sea,
  blueberry,
  grape,
  productCardSpecialization,
};
export { gradient };
const zIndex = {
  none: 0,
  xs: 1000,
  sm: 2000, // FixedContainer
  md: 3000, // SmartScrollWrapper
  lg: 4000,
  xl: 10000, // Critical, e.g. modal
  xxl: 10001,
};
export { zIndex };

const font = {
  xxs: '0.625rem',
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1.25rem',
  lg: '1.5rem',
  xl: '2.125rem',
  xxl: '3rem',
  xxxl: '4.5rem',
};
export { font };

const fontWeight = {
  light: '300',
  normal: '400',
  semibold: '600',
  bold: '700',
};
export { fontWeight };

const fontFamily = {
  body: 'OpenSans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  headline: "'OpenSans-Light', OpenSans, Arial, sans-serif",
  display: "'Merriweather-Light', Georgia, serif",
  bold: "'OpenSans-Bold', OpenSans, Arial, sans-serif",
  displayBold: "'Merriweather', Georgia, serif",
};
export { fontFamily };

const shimmerAnimationDuration = 1.3;

const shimmerKeyframes = {
  '0%': {
    backgroundPosition: '-468px 0',
  },
  '100%': {
    backgroundPosition: '468px 0',
  },
};

const shimmerStyles = StyleSheet.create({
  shimmer: {
    animationName: shimmerKeyframes,
    animationDuration: `${shimmerAnimationDuration}s`,
    animationIterationCount: 'infinite',
    animationDillMode: 'forwards',
    animationTimingFunction: 'linear',
    background: 'linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%)',
    backgroundSize: '1000px 104px',
    position: 'relative',
    overflow: 'hidden',
  },
});

const placeholder = {
  animationDuration: shimmerAnimationDuration,
  keyframes: shimmerKeyframes,
  styles: shimmerStyles,
};
export { placeholder };

export default {
  StyleSheet,
  css: cssWithClass,
  cssWithClass,
  transition,
  spacing,
  color,
  iconSize,
  breakPoint,
  containerMaxWidth,
  gradient,
  zIndex,
  font,
  fontWeight,
  fontFamily,
  placeholder,
};
